import { useEffect, useState } from "react";

import { UNASKED, type RoomPeek } from "@/engine/state/roomPeek";
import { peekRoom } from "@/adapters/supabase/roomPeek";
import { PREFETCH_MIN_CODE } from "@/adapters/supabase/roomPrefetch";

/** How long the typing has to stop before a code is worth looking up. */
const SETTLE_MS = 400;

/**
 * What is behind the code the visitor is typing, while they are typing it.
 *
 * The code settles before anything is opened. A channel per keystroke is a
 * channel per keystroke: the same reason `prefetchRoom` has a minimum length,
 * and the same minimum, because a code shorter than that is not a code yet.
 *
 * A report is kept with the code it is about. That is what makes "we know
 * nothing about this code" a thing the render can decide rather than a thing an
 * effect has to go and set: the moment the typed code stops matching the last
 * report, the last report stops being an answer.
 */
export function useRoomPeek(
  code: string,
  /**
   * True where the code is not being typed — a room's threshold looks behind
   * the code in its own address, and waiting for typing to stop that never
   * started is a delay with no reason.
   */
  { immediate = false }: { immediate?: boolean } = {},
): RoomPeek {
  const [report, setReport] = useState<{ code: string; peek: RoomPeek }>({
    code: "",
    peek: UNASKED,
  });
  const settled = code.trim().toLowerCase();

  useEffect(() => {
    if (settled.length < PREFETCH_MIN_CODE) return;

    let stop: (() => void) | null = null;
    const timer = window.setTimeout(
      () => {
        stop = peekRoom(settled, (peek) => setReport({ code: settled, peek }));
      },
      immediate ? 0 : SETTLE_MS,
    );

    // At most one room is being looked at, and none once this leaves the
    // screen: a visitor who types a code and thinks better of it must not
    // leave the first one subscribed behind them.
    return () => {
      window.clearTimeout(timer);
      stop?.();
    };
  }, [settled, immediate]);

  return report.code === settled ? report.peek : UNASKED;
}
